/**
 * The key to the board, drawn under it.
 *
 * WHAT IS LISTED. Only the elements this course actually has. The server
 * surveys the composed grid and sends the list with the layout; a player
 * looking up a tile in front of them should not have to read past twenty
 * elements printed on some other board.
 *
 * WHAT IS SHOWN. Art and a name. The rules text is the tooltip, which is
 * the only way forty elements fit under a board without pushing it off
 * the screen -- and the player who needs the paragraph is the one who has
 * already found the tile.
 *
 * THE ART is lifted's player aid, one 300px tile per element, scaled to
 * 150, with walls, one-way walls, board lasers, pushers, the
 * wrench-and-hammer site, radiation, radioactive waste and generators
 * supplied separately in the same form. It is the same art the boards are
 * drawn from, so a tile in the key and a tile on the board look alike.
 * Every element in the key has aid art; an element that does not would
 * need a schematic glyph drawn for it, in the flat vocabulary the board
 * schematics `tmx/board2svg.pl` use.
 *
 * THE TEXT is the player aid's, as the rules summary in `Help.tsx` is,
 * and follows it except where naming a thing outright reads better than
 * the aid's own allusion to it: the aid says highway and trails the
 * conveyor in a parenthetical, where the entry names both. Where the aid
 * pictures a thing the engine cannot tell apart from another -- a mine
 * shaft and a whirlpool are both pits, a traffic circle is a gear -- the
 * entries are merged and the art is shown side by side, because the data
 * has one kind and the boards draw it several ways.
 */

interface BoardKeyProps {
  /** Element keys, in the server's order. Anything not known here is
   * skipped rather than drawn blank: the server may be ahead of the
   * client after a deploy. */
  elements: string[];
  /**
   * What is lying on the board right now, so the key can explain it.
   *
   * The elements above are surveyed from the COURSE and never change; a
   * token arrives during play. Without these the key answered every
   * question a player could ask about the floor and none about the mine
   * sitting on it.
   */
  tokens?: { kind: string }[];
  /**
   * Whether debris is in play. The one thing that has to be listed
   * BEFORE it appears: it is not an Option anybody chose to bring, it
   * turns up the first time a robot is wrecked, and by then the player
   * wanting to know what the brown heap is has a turn to lose reading
   * about it.
   */
  debris?: boolean;
  /** Turn mode, which settles for itself what normal mode asks of the
   * player: those entries say so. */
  turnMode?: boolean;
}

interface Entry {
  name: string;
  /** Player aid art, by file name. Several where the boards draw one
   * element more than one way. */
  art?: string[];
  /** Which set the art comes from. Tokens are drawn from their own
   * folder, and are the same files the board itself draws, so a thing in
   * the key and the thing on the square look alike. */
  from?: 'elements' | 'tokens';
  text: string;
  /** The text in turn mode, where it differs. */
  turnText?: string;
}

const ENTRIES: Record<string, Entry> = {
  conveyor: {
    name: 'Conveyor',
    art: ['conveyor', 'highway'],
    text: 'Robots are moved 1 square on conveyors and single-lane highway, '
      + 'after movement. Liquids do not disperse on highways.',
  },
  expressConveyor: {
    name: 'Express conveyor',
    art: ['expressConveyor', 'expressHighway'],
    text: 'Robots are moved 2 squares on express conveyors and 2-lane highway, '
      + 'after movement. Liquids do not disperse on highways.',
  },
  current: {
    name: 'Current',
    art: ['current'],
    text: 'Robots move 1 square in current, after single conveyors.',
  },
  gear: {
    name: 'Gear',
    art: ['gear'],
    text: 'Rotates robot 90 degrees right (or left). Gears activate after '
      + 'pushers.',
  },
  pusher: {
    name: 'Pusher',
    art: ['pusher'],
    text: 'Active on the indicated register phases. Pushes a robot 1 square '
      + 'directly away, and can chain-push a line of robots the same way a '
      + 'card-driven push does. Pushers activate after conveyors and before '
      + 'gears.',
  },
  teleporter: {
    name: 'Teleporter',
    art: ['teleporter'],
    text: 'A robot executing a movement card on a teleporter appears as many '
      + 'squares forward as is indicated by the movement card, plus 2 squares. '
      + 'A backup will therefore teleport the robot forward 2 squares. '
      + 'If another robot or a non-flat device is in the destination square, the '
      + 'teleporter does not operate and the robot executes its movement card(s) '
      + 'normally.',
  },
  portal: {
    name: 'Portal',
    art: ['portal'],
    text: 'A robot that enters a portal during the execution of a movement card '
      + 'immediately moves to the other portal of the same color. If another '
      + "robot occupies the moving robot's destination portal, the moving robot "
      + 'continues to move as if the portal was open floor.',
  },
  stuntRamp: {
    name: 'Stunt ramp',
    art: ['stuntRamp'],
    text: 'A stunt ramp faces one direction with one low edge; the opposite side '
      + 'is the exit. The exit edge is treated as a wall for every purpose except '
      + 'a robot launching (or falling) off it. A robot standing on a stunt ramp '
      + 'square can still be targeted from any other direction. A robot leaving a '
      + 'stunt ramp square through the exit edge gains 1 level of elevation and 1 '
      + 'extra square of movement (2 squares of movement when from elevated '
      + 'terrain over non-elevated terrain). A robot that lands on another stunt '
      + 'ramp lands safely. A robot that lands anywhere else falls, taking damage '
      + 'as if it had fallen from elevated terrain. A robot pushed off the exit '
      + 'side falls into the adjacent square and takes 2 points of damage per '
      + 'level of elevation change.',
  },
  randomizer: {
    name: 'Randomizer',
    art: ['randomizer'],
    text: 'A robot on a randomizer when program cards are revealed replaces all '
      + 'program cards in that register with a card from the deck.',
  },
  pit: {
    name: 'Pit',
    art: ['pit', 'mineShaft', 'whirlpool'],
    text: 'Any robot falling into a pit is destroyed. A mine shaft and a '
      + 'whirlpool are also considered pits.',
  },
  trapDoorPit: {
    name: 'Trap door pit',
    art: ['trapDoorPit'],
    text: 'If a robot is on this square when the trap door pit is active, the '
      + 'trap door pit opens and the robot is destroyed. When not active, the '
      + 'trap door pit is treated as open floor. Millennium Falcon applies.',
  },
  crusher: {
    name: 'Crusher',
    art: ['crusher'],
    text: 'During laser fire, if a crusher is active, any robot or device on the '
      + 'crusher space is instantly destroyed.',
  },
  flamer: {
    name: 'Flamer',
    art: ['flamer'],
    text: 'When a robot moves onto or through an active flamer, or when a robot '
      + 'fails to leave an active flamer, the robot receives 1 point of damage. '
      + 'If a robot ends a register phase on an active flamer, the robot takes an '
      + 'additional 1 point of damage. Millennium Falcon applies.',
  },
  laser: {
    name: 'Board laser',
    art: ['laser'],
    text: '1 damage per laser occupying the square when weapons fire. A beam '
      + 'stops at the first robot in its path, so robots behind that one are '
      + 'shielded.',
  },
  radiation: {
    name: 'Radiation',
    art: ['radiation'],
    text: '1 damage at End of Turn Effects to a robot occupying the square at '
      + 'that moment: once per turn, not once per register.',
  },
  radioactiveWaste: {
    name: 'Radioactive waste',
    art: ['radioactiveWaste'],
    text: '1 damage during laser fire, and separately an Option card at the end '
      + 'of the register the robot is standing there. The draw is automatic, not '
      + 'a choice.',
  },
  generator: {
    name: 'Generator',
    art: ['generator'],
    text: 'A wrench-and-hammer repair site: heals 1 at End of Turn Effects and '
      + 'gives an option or upgrade. Archives the robot as any wrench does.',
  },
  wrench: {
    name: 'Single Wrench',
    art: ['single'],
    text: 'Heals 1. Archives the robot at the end of the Register Phase; the '
      + 'healing resolves at End of Turn Effects.',
  },
  doubleWrench: {
    name: 'Double Wrench',
    art: ['double'],
    text: 'Heals 2, or gives an option or upgrade instead. Archives the robot at '
      + 'the end of the Register Phase; the healing or the option resolves at '
      + 'End of Turn Effects.',
    turnText: 'Heals 2 if the robot has 2 or more damage; otherwise gives an '
      + 'option. Archives the robot at the end of a Register Phase; the healing '
      + 'or the option resolves at End of Turn Effects.',
  },
  repair: {
    name: 'Wrench/Hammer',
    art: ['repair'],
    text: 'Heals 1 and gives one option. Archives the robot at the end of the '
      + 'Register Phase; the healing and the option resolve at End of Turn '
      + 'Effects.',
  },
  chopShop: {
    name: 'Chop shop',
    art: ['chopShop'],
    text: 'If a robot ends a register phase on a chop shop, it may either scrap '
      + 'one option and draw a new one, or it may replenish ammunition on one of '
      + 'its options. Ending a TURN there, on register 5, adds a third choice: '
      + 'draw an option card outright, giving up nothing.',
    turnText: 'If a robot ends a register phase on a chop shop, it refills the '
      + 'ammunition of the option with the fewest shots left. Ending a TURN '
      + 'there, on register 5, draws an option card instead, unless none are '
      + 'left. In turn mode, there is no option exchange, or choice to reload '
      + 'on the 5th register.',
  },
  pitStop: {
    name: 'Pit stop',
    art: ['pitStop'],
    text: 'When a robot ends a register phase on a pit stop it removes a point of '
      + 'damage and discards programmed card(s) and tokens for the next register. '
      + 'The next register phase the robot will be completely inactive (even if '
      + 'no longer on a pit stop). A pit stop has no effect on register 5.',
  },
  restStop: {
    name: 'Rest stop',
    art: ['restStop'],
    text: 'When a robot ends a register phase on a Rest Stop without having '
      + 'executed a Move or Back-Up, it removes 1 point of damage.',
  },
  oil: {
    name: 'Oil slick',
    art: ['oil'],
    text: 'If a robot attempts to end its movement on an oil slick, it continues '
      + 'to slide in the direction of its movement until it is stopped by a wall '
      + 'or another robot not on an oil slick, or until it is no longer on an oil '
      + 'slick. If a robot slides into another robot that is on an oil slick, '
      + 'both robots slide as described above. Note that a robot does not slide '
      + 'until it attempts to end its movement on an oil slick; robots that are '
      + 'still moving behave in the normal manner. If a robot begins its movement '
      + 'on an oil slick, the first square of movement is negated. Oil slicks '
      + 'have no effect on rotate cards.',
  },
  flamingOil: {
    name: 'Flaming oil',
    art: ['flamingOil'],
    text: 'Flaming oil works as a combination of oil and flamers, and follows the '
      + 'rules for oil in regards to movement. A robot will receive a point of '
      + 'damage when it enters the first square of flaming oil during movement. A '
      + 'robot in flaming oil during laser fire will receive an additional point '
      + 'of damage (a total of 2 points of damage if the robot slides in flaming '
      + 'oil and ends the register phase in flaming oil).',
  },
  spikes: {
    name: 'Spikes',
    // TWO ELEMENTS, ONE KIND. `tiles.yml` maps both RallyX tiles -- the
    // caltrops and the studded floor plate -- onto `kind: spikes`, so
    // nothing downstream can tell them apart and neither can this key.
    // Two entries would therefore both appear on a course carrying
    // either one. Merged instead, as a pit is, with both drawings shown:
    // a player who found either on the board finds either here.
    art: ['spikes', 'spikedFloor'],
    text: 'A robot entering or rotating on a square with spikes will receive a '
      + 'point of damage. A spiked floor is also considered spikes.',
  },
  speedBump: {
    name: 'Speed bump',
    art: ['speedBump'],
    text: 'A robot that moves or is pushed onto a speed bump with a Move 2 or '
      + 'Move 3 receives a point of damage. A robot that slides onto a speed bump '
      + 'as part of a Move 2 or Move 3 is also damaged.',
  },
  smoke: {
    name: 'Smoke',
    art: ['smoke'],
    text: 'Laser fire will not penetrate smoke. A robot cannot target or be '
      + 'targeted in or through smoke. Smoke is a vapor.',
  },
  gravel: {
    name: 'Gravel',
    art: ['gravel'],
    text: 'A robot executing a Rotate Right (or Rotate Left) on gravel will then '
      + 'slide 1 square in the opposite direction of the turn. Functionally this '
      + 'typically appears similar to Move 1 and Rotate in the direction of the '
      + 'Rotate card.',
  },
  mud: {
    name: 'Mud',
    art: ['mud'],
    text: 'A robot that stops in mud will slide (as oil) 1 square, but only 1 '
      + 'square. A robot executing a Move 2 (or Move 3) when starting movement in '
      + 'mud will not move.',
  },
  sand: {
    name: 'Sand',
    art: ['sand'],
    text: 'A robot that executes a Move 3 in sand will not move. A robot '
      + 'executing a Move 3 that enters sand will stop in the first square of '
      + 'sand.',
  },
  water: {
    name: 'Water',
    art: ['water'],
    text: 'A robot leaving a water square via a movement card has the first '
      + 'square of movement negated once per register phase: a robot in water '
      + 'executing a Move 1 or Back-Up will not move; a robot executing a Move 2 '
      + 'in water will move 1 square. Sliding in water counts as a square of '
      + 'movement.',
  },
  dirt: { name: 'Dirt', art: ['dirt'], text: 'As open factory floor.' },
  grass: { name: 'Grass', art: ['grass'], text: 'As open factory floor.' },
  wall: {
    name: 'Wall',
    art: ['wall'],
    text: 'Blocks movement across the edge, in both directions, and stops laser '
      + 'fire and everything else that travels in a line.',
  },
  oneWayWall: {
    name: 'One-way wall',
    art: ['oneWayWall'],
    text: 'Solid from its red side and open from the green side. Fire passes it '
      + 'the same way movement does: through from the green side, stopped from '
      + 'the red.',
  },
  spikedWall: {
    name: 'Spiked wall',
    art: ['spikedWall'],
    text: 'A robot entering a square with a spiked wall receives no damage. A '
      + 'robot that moves, slides, or is pushed (even if the robot did not '
      + 'actually move any squares) into a spiked wall will receive a point of '
      + 'damage.',
  },
  repulsor: {
    name: 'Repulsor field',
    art: ['repulsor'],
    text: 'An edge feature that throws a robot back. A robot that moves into a '
      + 'field is pushed straight back the way it came, as many squares as the '
      + 'movement that ran into it was worth, and loses the rest of that card. A '
      + 'robot shoved into a field is thrown back by the pushing card value '
      + 'instead, and a thrown robot can push others. Once a side has fired it is '
      + 'a wall for the rest of that register. Each repulsor field will activate '
      + 'only once per register phase per side. A sliding robot will trigger a '
      + 'repulsor as a Move card with a value equal to the number of squares of '
      + 'the slide. A repulsor does not block weapons fire.',
  },
  cliff: {
    name: 'Cliff/Ledge',
    art: ['cliff', 'cliffDirt'],
    text: 'When a robot crosses over a ledge from the upper level to the lower '
      + 'level, the robot lands, stops, and receives 2 damage. When a robot '
      + 'approaches a ledge from the lower level, treat the ledge as a wall.',
  },
  ramp: {
    name: 'Ramp',
    art: ['ramp', 'rampDirt'],
    text: 'When a robot is moving up a ramp from the lower level, treat '
      + 'the ramp as an extra square of open floor. When a pushed robot slides '
      + 'back down a ramp, it will physically push any other robots sitting '
      + 'directly on the lower level behind it.',
  },
  bollard: {
    name: 'Bollards',
    art: ['bollard'],
    text: 'Bollards are active on the indicated register phases. Bollards '
      + 'function as walls when active, and as open floor when inactive.',
  },
};

/**
 * Things that arrive on the board during play, by the `kind` the view
 * sends, keyed separately from the elements above.
 *
 * A kind that is also board terrain -- oil, flaming oil, smoke, water,
 * mud -- is NOT here: it reads the element entry instead, so the rule is
 * stated once whether the square was printed that way or dropped that
 * way. `TOKEN_ELEMENT` below is that mapping.
 *
 * The text is each Option's own printed wording, from the card that drops
 * the thing, as `ENTRIES` is the player aid's.
 */
const TOKEN_ENTRIES: Record<string, Entry> = {
  debris: {
    name: 'Debris',
    art: ['debris'],
    from: 'tokens',
    text: 'Debris appears where a robot is destroyed, or receives 2 or more '
      + 'damage during a register phase. A robot that enters and exits a '
      + 'debris square during a single register phase receives 1 damage; '
      + 'entering and stopping there is harmless. The debris is removed as '
      + 'soon as it causes damage.',
  },
  spike: {
    name: 'Spike token',
    art: ['spike'],
    from: 'tokens',
    text: 'The next robot to rotate on or move into that square receives a '
      + 'point of damage. A spike token is destroyed when causing damage.',
  },
  goo: {
    name: 'Goo',
    art: ['goo'],
    from: 'tokens',
    text: 'If a robot passes over or stops on the goo, the robot cannot leave '
      + 'that square until it attempts to move a total of four squares in any '
      + 'direction. If a robot is pushed while on the goo, that counts towards '
      + 'the four squares. Robots may rotate freely on goo.',
  },
  mine: {
    name: 'Mine',
    art: ['mine'],
    from: 'tokens',
    text: 'If a robot passes over or stops on the mine, the mine explodes for '
      + '4 points of damage.',
  },
  proximityMine: {
    name: 'Proximity mine',
    art: ['proximityMine'],
    from: 'tokens',
    text: 'If a non-flying robot passes within 1 square of the mine it '
      + 'explodes for 4 points of damage.',
  },
  portableTeleporter: {
    name: 'Portable teleporter',
    art: ['portableTeleporter'],
    from: 'tokens',
    text: 'Treat the portable teleporter as if it were a teleporter board '
      + 'element. A portable teleporter is active for all robots the moment it '
      + 'is launched.',
  },
  scramblerBomb: {
    name: 'Scrambler bomb',
    art: ['scramblerBomb'],
    from: 'tokens',
    text: 'At the beginning of the next turn, all robots within 6 squares of '
      + 'the bomb execute program cards at random for the entire turn.',
  },
  seismicCharge: {
    name: 'Seismic charge',
    art: ['seismicCharge'],
    from: 'tokens',
    text: 'The charge detonates just prior to the end of the NEXT turn. Walls '
      + 'and elevation do not block damage from a seismic charge. The '
      + 'detonation also creates a debris token in the square. 3 points of '
      + 'damage.',
  },
  missile: {
    name: 'Missile',
    art: ['missile'],
    from: 'tokens',
    text: 'During each subsequent phase, move the missile forward 2 squares. '
      + 'If the missile collides with a robot or wall, the missile explodes '
      + 'immediately for 4 points of damage.',
  },
  bridge: {
    name: 'Bridge',
    art: ['bridge'],
    from: 'tokens',
    text: 'Treat all robots moving over this square as if they were moving '
      + 'over open floor for the rest of the game. Bridges may only be placed '
      + 'over pits, including timed pits, mine shafts and whirlpools.',
  },
};

/** Token kinds the boards also print as terrain. One entry serves both:
 * the rule does not change with how the square came to be that way. */
const TOKEN_ELEMENT: Record<string, string> = {
  oil: 'oil',
  flamingOil: 'flamingOil',
  smoke: 'smoke',
  water: 'water',
  mud: 'mud',
  radiation: 'radiation',
  radioactiveWaste: 'radioactiveWaste',
};

export function BoardKey({
  elements, tokens = [], debris = false, turnMode = false,
}: BoardKeyProps) {
  // The course's own elements first, in the server's order, then whatever
  // has since been dropped on them. Debris leads the second group: it is
  // the one thing listed before it appears, and the one thing in it that
  // nobody chose to bring.
  const keys = [...elements];
  const add = (key: string) => { if (!keys.includes(key)) keys.push(key); };
  if (debris) add('debris');
  for (const token of tokens) add(TOKEN_ELEMENT[token.kind] ?? token.kind);

  const shown = keys
    .map((key) => ({ key, entry: ENTRIES[key] ?? TOKEN_ENTRIES[key] }))
    .filter((e): e is { key: string; entry: Entry } => Boolean(e.entry));
  if (shown.length === 0) return null;

  return (
    <section className="board-key" aria-label="Key to the board">
      {/* The hazard rule is the only heading it gets: a grid of board
          tiles under a board needs no label saying what it is. */}
      <div className="hazard hazard-thin" role="presentation" />
      <ul className="key-grid">
        {shown.map(({ key, entry }) => (
          // An entry with more than one tile takes two columns rather than
          // squeezing them into one: three pits shrunk to fit a single
          // column read as smaller elements, not as the same one drawn
          // three ways.
          <li
            key={key}
            className={(entry.art?.length ?? 0) > 1 ? 'key-item wide' : 'key-item'}
            title={(turnMode && entry.turnText) || entry.text}
          >
            <div className="key-art">
              {entry.art?.map((file) => (
                <img
                  key={file}
                  src={`/${entry.from ?? 'elements'}/${file}.png`}
                  alt=""
                  loading="lazy"
                />
              ))}
            </div>
            <span>{entry.name}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}
